import { connect as withFormik } from 'formik'
import { useEffect, useState } from 'react'
import AnimateHeight from 'react-animate-height'
import { FormattedMessage, useIntl } from 'react-intl'

import { Checkbox, FormattedHeader, Radio, Text } from '@/components/Form'
import UserInterfaceLayout from '@/components/UserInterfaceLayout'
import { LayoutOptions, SandboxOptions } from '@/pages/SpaceCustomAppEditPage/constants'
import AudienceFields from '@/pages/SpaceCustomAppEditPage/CustomAppEditView/AudienceFields'
import DomainWhitelistField from '@/pages/SpaceCustomAppEditPage/CustomAppEditView/DomainWhitelistField'

import messages from './messages'
import { appFormPropTypes } from './propTypes'

import 'react-toggle/style.css'

import { isEmpty } from 'lodash'
import Toggle from 'react-toggle'
import { usePrevious } from 'react-use'

import DestructiveConfirmationOverlay from '@/components/DestructiveConfirmationOverlay'
import useFeatures from '@/components/useFeatures'
import { APP_STORE_EXPANDED_BY_DEFAULT } from '@/constants/featureToggles'

function MicroUIFormFields(props: any) {
  const intl = useIntl()

  const features = useFeatures()

  const [state, _setState] = useState({
    isAudienceTargetingExpanded: false,
    isMicroUIExpanded: false,
    isAppInfoDefaulted: false,
    disableAppUi: false
  })
  const setState = (update: Partial<Parameters<typeof _setState>[0]>) => {
    return _setState(state => ({ ...state, ...(typeof update === 'function' ? update(state) : update) }))
  }

  // componentDidMount
  useEffect(() => {
    setToggleAndDefaultsCheckbox()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // componentDidUpdate
  const prevProps = usePrevious(props)
  useEffect(() => {
    if (!prevProps) return

    if (prevProps.formik.values.currentMicroapp.id !== props.formik.values.currentMicroapp.id) {
      setToggleAndDefaultsCheckbox()
    }

    if (state.isAppInfoDefaulted && prevProps.formik.values.currentApp !== props.formik.values.currentApp) {
      setTimeout(() => props.formik.setFieldValue('currentMicroapp.title', props.formik.values.currentApp.name), 1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props, prevProps])

  function setToggleAndDefaultsCheckbox() {
    const doesMicroAppExist = !isEmpty(props.formik.values.currentMicroapp.id)
    let doesAudienceTargetExist = false
    if (doesMicroAppExist) {
      doesAudienceTargetExist =
        !isEmpty(props.formik.values.currentMicroapp.audience) &&
        (!isEmpty(props.formik.values.currentMicroapp.audience[0].filters) ||
          !isEmpty(props.formik.values.currentMicroapp.audience[0].audienceFilter))
    }

    setState({
      isMicroUIExpanded: doesMicroAppExist,
      isAudienceTargetingExpanded: doesAudienceTargetExist
    })
    // need to wrap in setTimeout to apply formik setFieldValue in react CDM https://github.com/jaredpalmer/formik/issues/930
    setTimeout(() => props.formik.setFieldValue('isMicroUIExpanded', doesMicroAppExist), 1)
    setTimeout(() => props.formik.setFieldValue('isAudienceTargetingExpanded', doesAudienceTargetExist), 1)
    // Convert to string representation for radio input
    setTimeout(
      () =>
        props.formik.setFieldValue(
          'currentMicroapp.configuration.allowDetailExpand',
          `${props.formik.values.currentMicroapp.configuration.allowDetailExpand}`
        ),
      1
    )

    const link = props.formik.values.currentMicroapp.configuration.contact.link
    if (link.startsWith('mailto:')) {
      setTimeout(
        () => props.formik.setFieldValue('currentMicroapp.configuration.contact.link', link.replace('mailto:', '')),
        1
      )
    }

    const isAppInfoDefaulted = props.formik.values.currentMicroapp.title === props.formik.values.currentApp.name
    setState({ isAppInfoDefaulted: isAppInfoDefaulted })

    if (isAppInfoDefaulted) {
      setTimeout(() => props.formik.setFieldValue('currentMicroapp.defaultsSelected', ['defaults-selected']), 1)
    }
  }

  const microUIExpandedHandleChange = (e: any) => {
    const isExpanded = e.target.value !== 'true'
    const isEditingExistingMicroapp = props.formik.values.currentMicroapp.id
    if (!isExpanded && isEditingExistingMicroapp) {
      setState({ disableAppUi: true })
    } else {
      setMicroUIExpandedState(isExpanded)
    }
  }

  const setMicroUIExpandedState = (isExpanded: any) => {
    setState({ isMicroUIExpanded: isExpanded })
    props.formik.setFieldValue('isMicroUIExpanded', isExpanded)
    !isExpanded && undoToggleOff()
  }

  const undoToggleOff = () => setState({ disableAppUi: false })

  const toggleExpandedByDefault = (e: any) => {
    const { checked } = e.target
    props.formik.setFieldValue('currentMicroapp.configuration.expandedByDefault', !!checked)
  }

  const checkAllowDetailExpandStatus = (e: any) => {
    if (features[APP_STORE_EXPANDED_BY_DEFAULT] && e.target.value === 'false') {
      props.formik.setFieldValue('currentMicroapp.configuration.expandedByDefault', false)
    }
  }

  const audienceTargetExpandedHandleChange = (e: any) => {
    const isExpanded = e.target.value !== 'true'

    setState({ isAudienceTargetingExpanded: isExpanded })
    props.formik.setFieldValue('isAudienceTargetingExpanded', isExpanded)
  }

  const toggleDefaults = () => {
    const isDefault = !state.isAppInfoDefaulted
    setState({ isAppInfoDefaulted: isDefault })

    if (isDefault) {
      props.formik.setFieldValue('currentMicroapp.title', props.formik.values.currentApp.name)
    } else {
      props.formik.setFieldValue('currentMicroapp.title', '')
    }
  }

  const { formik } = props
  return (
    <div className="app-form-fields">
      <div className="additional-fields__label">
        <div className="additional-fields__row accordion-title">
          <FormattedMessage {...messages.microUI} />
          <Toggle
            checked={state.isMicroUIExpanded}
            name="isMicroUIExpanded"
            value={String(state.isMicroUIExpanded)}
            onChange={microUIExpandedHandleChange}
          />
          {!!state.disableAppUi && (
            <DestructiveConfirmationOverlay
              onConfirm={() => setMicroUIExpandedState(false)}
              onCancel={undoToggleOff}
              onConfirmActionTitle={<FormattedMessage {...messages.disableConfirm} />}
              message={<FormattedMessage {...messages.disableCheck} />}
              subMessage={<FormattedMessage {...messages.disablingAppUserInterface} />}
              confirmIsPrimary={true}
            />
          )}
        </div>
        <div className="additional-fields-summary__label columnHeader">
          {!state.isMicroUIExpanded && (
            <>
              <span className="subcomponent-text">
                <FormattedMessage {...messages.microUIDescription} />
              </span>
              <span>
                <FormattedMessage {...messages.subcomponentDescriptionOff} />
              </span>
            </>
          )}
        </div>
      </div>
      <AnimateHeight duration={500} height={state.isMicroUIExpanded ? 'auto' : 0} animateOpacity>
        <div className="rowApp">
          <div className="columnHeader">
            <Radio
              name="currentMicroapp.configuration.allowDetailExpand"
              label={<FormattedMessage {...messages.allowDetailExpand} />}
              required
              tooltip={<UserInterfaceLayout />}
              oversizedTooltip={true}
            >
              <div className="micro-app-layout flex">
                {Object.values(LayoutOptions).map(layoutOptions => (
                  <Radio.Input
                    name="currentMicroapp.configuration.allowDetailExpand"
                    id={layoutOptions}
                    key={layoutOptions}
                    label={layoutOptions}
                    value={layoutOptions === LayoutOptions.EXPANDABLE ? 'true' : 'false'}
                    onChange={(e: any) => checkAllowDetailExpandStatus(e)}
                  />
                ))}
                {formik.values.currentMicroapp.configuration.allowDetailExpand === 'true' &&
                  features[APP_STORE_EXPANDED_BY_DEFAULT] && (
                    <Checkbox.Input
                      label="Expanded by Default"
                      name="currentMicroapp.configuration.expandedByDefault"
                      value={true}
                      id="expandedByDefault"
                      onChange={e => toggleExpandedByDefault(e)}
                    />
                  )}
              </div>
            </Radio>
            <div className="form__field--required">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="field__label">
                <FormattedHeader
                  label={<FormattedMessage {...messages.appDisplayName} />}
                  name="header"
                  required
                  tooltip={<FormattedMessage {...messages.titleTooltip} />}
                />
              </label>
            </div>
            <Checkbox name="currentMicroapp.defaultsSelected" className="default-checked">
              <Checkbox.Input
                className="microapp-default-name"
                id="microapp-default-name"
                data-testid="microapp-default-name"
                key="microapp-default-name"
                label={<FormattedMessage {...messages.defaultName} />}
                name="currentMicroapp.defaultsSelected"
                value="defaults-selected"
                onChange={toggleDefaults}
              />
            </Checkbox>
            <div className="child-fields">
              <AnimateHeight duration={50} height={state.isAppInfoDefaulted ? 0 : 'auto'} animateOpacity>
                <Text
                  name="currentMicroapp.title"
                  required
                  placeholder={intl.formatMessage(messages.titlePlaceholder)}
                />
              </AnimateHeight>
            </div>
            <Text
              label={<FormattedMessage {...messages.description} />}
              name="currentMicroapp.summary"
              required
              placeholder={intl.formatMessage(messages.descriptionPlaceholder)}
              tooltip={<FormattedMessage {...messages.descriptionTooltip} />}
            />
            <div className="field__label">
              <FormattedMessage {...messages.sandboxOptions} />
            </div>
            <div className="containerApp">
              <Checkbox name="currentMicroapp.configuration.securityOptions">
                {Object.values(SandboxOptions).map(securityOptions => (
                  <Checkbox.Input
                    id={securityOptions}
                    key={securityOptions}
                    label={securityOptions}
                    data-testid={securityOptions}
                    name="currentMicroapp.configuration.securityOptions"
                    value={securityOptions}
                    className={`${securityOptions}-filter`}
                  />
                ))}
              </Checkbox>
            </div>
          </div>
        </div>
        <hr className="additional-fields__divider" />
        <div className="rowApp">
          <div className="columnHeader">
            <Text
              label={<FormattedMessage {...messages.contact} />}
              name="currentMicroapp.configuration.contact.link"
              required
              placeholder={intl.formatMessage(messages.contactPlaceholder)}
            />
            <Text
              label={<FormattedMessage {...messages.url} />}
              name="currentMicroapp.url"
              required
              placeholder={intl.formatMessage(messages.urlPlaceHolder)}
              tooltip={<FormattedMessage {...messages.urlTooltip} />}
              type="url"
            />
          </div>
        </div>
        <hr className="additional-fields__divider" />
        <div className="rowApp">
          <div className="columnHeader">
            <DomainWhitelistField
              tooltip={<FormattedMessage {...messages.domainWhiteListToolip} />}
              label={<FormattedMessage {...messages.domainWhitelist} />}
              name="currentMicroapp.configuration.domainWhitelist"
            />
          </div>
        </div>
        <>
          <div className="additional-fields__label">
            <div className="additional-fields__row accordion-title">
              <FormattedMessage {...messages.audienceTargeting} />
              <Toggle
                checked={state.isAudienceTargetingExpanded}
                name="isAudienceTargetingExpanded"
                value={String(state.isAudienceTargetingExpanded)}
                onChange={audienceTargetExpandedHandleChange}
              />
            </div>
          </div>
          <div className="additional-fields-summary__label columnHeader">
            {!state.isAudienceTargetingExpanded && (
              <>
                <span className="subcomponent-text">
                  <FormattedMessage {...messages.audienceTargetingDescription} />
                </span>
                <span>
                  <FormattedMessage {...messages.audienceTargetingSubComponentDescription} />
                </span>
              </>
            )}
          </div>
          <AnimateHeight duration={100} height={state.isAudienceTargetingExpanded ? 'auto' : 0}>
            <AudienceFields name="currentMicroapp.audience" />
          </AnimateHeight>
        </>
      </AnimateHeight>
    </div>
  )
}

MicroUIFormFields.displayName = 'MicroUIFormFields'
MicroUIFormFields.propTypes = {
  ...appFormPropTypes
}

export default withFormik(MicroUIFormFields)
